<script>
/**
 * Imports
 */
import { has } from "lodash";
import Validators from "./../../helpers/validators.js";

/**
 * Component: Input
 *
 * Component used for displaying the input fields based
 * on the page configuration. Each instance operates on
 * global state managed by Vuex.
 *
 * @author PH
 */
export default {
  props: [
    "id",
    "type",
    "section",
    "options",
    "placeholder",
    "description",
    "validations",
    "settings"
  ],

  data: function() {
    return {
      focused: false,
      validationResponse: null
    };
  },

  mounted: function() {
    // console.log( 'asd' );
    // this.$v.$pending;
  },

  computed: {
    value: {
      get() {
        return this.$store.getters.getFieldValue(this.id, this.section);
      },
      set(value) {
        this.$store.commit("updateInput", {
          id: this.id,
          section: this.section,
          values: {
            value: value
          }
        });
      }
    },

    valid: {
      get() {
        return this.$store.getters.getFieldData(this.id, "valid", this.section);
      },
      set(value, event) {
        this.$store.commit("updateInput", {
          id: this.id,
          section: this.section,
          values: {
            valid: value
          }
        });
      }
    },

    visible: {
      get() {
        return this.$store.getters.getFieldData(this.id, "visible", this.section);
      },
      set(value) {
        this.$store.commit("updateInput", {
          id: this.id,
          section: this.section,
          values: {
            visible: value
          }
        });
      }
    },

    /**
     * Input: Classes
     *
     * Functions creates the classes object for this input.
     *
     * @author PH
     * @return Object
     */
    classes() {
      const classes = {};

      if (this.valid === true) {
        classes["signup-form-input--valid"] = true;
      }

      if (this.focused === true || this.value.length > 0) {
        classes["signup-form-input--focused"] = true;
      }

      if (this.valid === false) {
        classes["signup-form-input--invalid"] = true;
      }

      if (this.value) {
        if (this.$v.$pending === true && this.value) {
          classes["signup-form-input--pending"] = true;
        }
      }

      if (this.settings) {
        if (this.settings.size) {
          classes["signup-form-input--" + this.settings.size] = true;
        }

        if (this.settings.styled) {
          classes["signup-form-input--styled"] = true;
        }
      }

      return classes;
    },

    /**
     * Input: Error
     *
     * This field loads the correct error based on validator.
     * The error messages are set in data spurce.
     *
     * @author PH
     * @return string
     */
    error() {
      let error = "";

      Object.keys(Validators).forEach(type => {
        if (this.$v.value.hasOwnProperty(type) && !this.$v.value[type]) {
          error = this.validations[type];
        }
      });

      return error;
    },

    /**
     * Returns readonly attribute value
     *
     * @author PH
     * @return bool
     */
    readonly() {
      if (has(this.settings, "readonly")) {
        return this.settings.readonly;
      }

      return false;
    }
  },

  watch: {
    value: function(value) {
      this.updateValid();
    },

    // Send information about received validation data.
    validationResponse: function() {
      this.$store.commit("handleValidationResponse", {
        trigger: this.id,
        validationResponse: this.validationResponse
      });
    }
  },

  /**
   * Input: Validate
   *
   * Function enables the field validation with vuelidator.
   * It is based on the field param called `validations`.
   *
   * @see https://vuelidate.netlify.com/#sub-dynamic-validation-schema
   * @author PH
   */
  validations() {
    const rules = {};

    if (this.validations && Validators) {
      Object.keys(this.validations).forEach(type => {
        if (Validators[type]) {
          rules[type] = Validators[type];
        }
      });
    }

    return {
      value: rules
    };
  },

  methods: {
    /**
     * Function updates the valid key after validation
     * and updating value field. For async validation
     * we need to vait until function finish.
     *
     * @author PH
     * @return void
     */
    async updateValid() {
      if (has(this.settings, "validation.api")) {
        this.valid = await this.waitForValidation();
      }

      this.valid = !this.$v.$invalid;
    },

    /**
     * Function checks if input should be shown.
     * It needs to make a few operations on the valid
     * data to allow submitting the form when field
     * is hidden;
     *
     * @author PH
     * @return bool
     */
    isVisible() {
      let result = true;

      if (this.settings.conditional) {
        result = this.conditionalValid();
        this.visible = result;

        // Disable validation for hidden inputs.
        if (false === result) {
          this.value = "";
          this.valid = true;
        } else {
          if (this.value) {
            this.valid = true;
          } else {
            this.valid = false;
          }
        }
      }

      return result;
    },

    /**
     * Function checks if conditionals are valid. It analyzes
     * the values of the required inputs and returns correct
     * state.
     *
     * @author PH
     * @return bool
     */
    conditionalValid() {
      const result = this.$store.getters.getConditionalChecker( this.settings.conditional );

      if (result !== undefined) {
        return result;
      }

      return true;
    },

    /**
     * Active focus state.
     *
     * @author PH
     * @return void
     */
    addFocus() {
      this.focused = true;
    },

    /**
     * Dective focus state.
     *
     * @author PH
     * @return void
     */
    removeFocus() {
      this.focused = false;
    },

    /**
     * Function sets the value of the field with
     * external event;
     *
     * @author PH
     * @return void
     */
    appendValue(value) {
      this.value = value;
    },

    /**
     * Function waits for validation finishing.
     *
     * @author PH
     * @return {Promise}
     */
    waitForValidation() {
      return new Promise(resolve => {
        const unwatch = this.$watch(
          () => !this.$v.$pending,
          isNotPending => {
            if (isNotPending) {
              resolve(!this.$v.$invalid);
            }
          }
        );
      });
    },

    /**
     * This cunction can be used for setting the
     * data returned during validation proces.
     *
     * @param {object} response API Response.
     * @return {void}
     */
    setValidationResponse(response) {
      this.validationResponse = response;
    }
  }
};
</script>

<template>
  <div class="signup-form-input" :class="classes" v-if="isVisible()" :data-type="type">
    <div class="signup-form-input__object">
      <div
        :class="['signup-form-input-' + type]"
        v-if="'text' === type || 'email' === type || 'number' === type || 'date' === type || 'url' === type || 'tel' === type"
      >
        <label :for="id">{{placeholder}}</label>
        <input
          :type="type"
          :name="id"
          :id="id"
          :readonly="readonly"
          v-model.lazy="value"
          @focus="addFocus"
          @blur="removeFocus"
        />
      </div>

      <div class="signup-form-input-checkbox" v-if="'checkbox' === type">
        <div class="signup-form-input-checkbox__item">
          <input type="checkbox" :name="id" :id="id" v-model="value" true-value="1" false-value="" />
          <label :for="id">{{placeholder}}</label>
        </div>
      </div>

      <div class="signup-form-input-radio" v-if="'radio' === type">
        <div class="signup-form-input-radio__label">
          <span v-html="placeholder"></span>
        </div>
        <div class="signup-form-input-radio__items">
          <div
            class="signup-form-input-radio__item"
            v-for="option in options"
            v-bind:key="option.id"
          >
            <input
              type="radio"
              :name="id"
              :id="id+'_'+option.id"
              :value="option.value"
              v-model="value"
            />
            <button
              type="button"
              @click="appendValue(option.value)"
              :class="[{checked: value === option.value}]"
            >{{option.label}}</button>
          </div>
        </div>
      </div>

      <div class="signup-form-input-select" v-if="'select' === type">
        <div class="signup-form-input-select__item">
          <select :name="id" :id="id" :readonly="readonly" v-model="value">
            <option
              v-for="option in options"
              v-bind:key="option.value"
              :value="option.value"
            >{{option.label}}</option>
          </select>
          <label :for="id">{{placeholder}}</label>
        </div>
      </div>

      <div class="signup-form-input-textarea signup-form-input__single" v-if="'textarea' === type">
        <div class="signup-form-input-textarea__item">
          <textarea
            v-if="'textarea' === type"
            v-model="value"
            :name="id"
            :id="id"
            :placeholder="placeholder"
            :readonly="readonly"
            rows="4"
          ></textarea>
        </div>
      </div>
    </div>

    <div class="signup-form-input__description" v-if="description">
      <p v-html="description"></p>
    </div>

    <div class="signup-form-input__error" v-if="valid === false">
      <span>{{error}}</span>
    </div>
  </div>
</template>



